import React, { useEffect, useState } from "react";
import MyBreakcrumbs from "../components/MyBreadcrumbs";
import MyTable from "../components/MyTable";
import SearchInput from "../components/SearchInput";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { useNavigate } from "react-router-dom";
import { Button, Tab, Tabs, TextField } from "@mui/material";

import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { CustomInput, downloadFile, formatDateGlobal, getStartOrEndOfDay } from "../utils/commonUtils";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getClinicIdFromToken } from "../utils/tokenUtils";
import { fetchReportsByClinicId, serviceFetchDoctorsList } from "../services/api";
import CustomSelect from "../components/CustomSelect";
import { showToast } from "../utils/toastUtils";
import dayjs from "dayjs";
import AntDatePicker from "../components/antDatepicker";

const Report = () => {
  const navigate = useNavigate();
  const [selectedDoctor, setSelectedDoctor] = useState({ value: null, label: 'All Doctors' });
  const [doctors, setDoctors] = useState([]);
  const [reports, setReports] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const token = localStorage.getItem("token");
  const decodedToken = jwtDecode(token);

  const [startDate, setStartDate] = useState(dayjs(getStartOrEndOfDay(new Date(), 'start')));
  const [endDate, setEndDate] = useState(dayjs(getStartOrEndOfDay(new Date(), 'end')));
  const [selectedTab, setSelectedTab] = useState("today"); // State for selected tab

  const handlePrescriptionClick = (patientId) => {
    // Navigate to the prescription generation page with the patient ID
    navigate(`/clinic/generatePrescription?patientId=${patientId}`);
  };

  const columns = [
    { Header: "ID", accessor: "invoiceitemid" },
    { Header: "Invoice ID", accessor: "invoiceid" },
    { Header: "Patient", accessor: "patientname" },
    // {
    //   Header: "Date",
    //   accessor: "date",
    //   Cell: ({ value }) => formatDateGlobal(value),
    // },
    { Header: "Description", accessor: "description" },
    { Header: "Discount", accessor: "discount" },
    { 
      Header: "Doctor", 
      accessor: "doctorid", 
      Cell: ({ value }) => {
        const doctor = doctors.find((doctor) => doctor.doctor_id === value);
        return doctor ? `${doctor.first_name} ${doctor.last_name}` : '';
      },
    },
    { Header: "Total Amount", accessor: "totalamount" },
  ];
  
  const getReports = async () => {
    try {
      const clinicId = getClinicIdFromToken();
      const filters = { startDate:getStartOrEndOfDay(startDate,'startdayjs'), endDate:getStartOrEndOfDay(endDate,'enddayjs'), doctorId: selectedDoctor?.value };
      const response = await fetchReportsByClinicId(clinicId, filters);
      if(response?.status) {
        setReports(response?.data);
      }
    } catch (error) {
      console.error("Error fetching reports: ", error);
    }
  };

  const fetchDoctors = async () => {
    try {
      const clinicId = getClinicIdFromToken();
      const response = await serviceFetchDoctorsList(clinicId);
      if(response?.status) {
        setDoctors(response?.data?.doctors || []);
      }
    } catch (error) {
      console.error("Error fetching doctors: ", error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);
  
  useEffect(() => {
    if (doctors) {
      getReports();
    }
  }, [selectedTab, startDate, endDate, doctors, selectedDoctor]);

  const handleFilterChange = (event) => {
    const value = event.target.value || "";
    setFilterInput(value);
  };

  const handleExport = () => {
    if(reports.length === 0) {
      showToast("No data to export", "error");
      return;
    }
    
    // Get headers (column names)
    const headers = Object.keys(reports[0]);

    const updatedHeaders = headers.map((header) => {
      if (header === "doctorid") {
        return "doctor";
      }
      return header;
    });

    // Create a map of doctors by id
    const doctorMap = new Map(doctors.map(doctor => [doctor.doctor_id, doctor]));

    // Create a new array with updated data
    const csvData = reports.map((report) => {
      // Replace doctorId with doctorName
      const doctor = doctorMap.get(report.doctorid);
      if (doctor) {
        report.doctorid = doctor.first_name + " " + doctor.last_name;
      }
      return Object.values(report).join(",");
    });

    // Add headers to the start of the csvData array
    csvData.unshift(updatedHeaders.join(","));

    // Create a blob containing the CSV data
    const blob = new Blob([csvData.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);
    
    downloadFile(url, "reports.csv");
  };

  // Calculate total amount
  const totalAmount = reports.reduce(
    (total, report) => total + parseFloat(report.totalamount),
    0
  );

  // Function to extract unique doctor names from filtered reports
  const extractUniqueDoctors = (filteredReports) => {
    const doctors = new Set();
    filteredReports.forEach((report) => {
      doctors.add(report.doctor);
    });
    return Array.from(doctors);
  };

  const breadcrumbs = [
    {
      label: "Home",
      href: "#",

      class: "text-[#2E37A4]",
    },
    { label: "Report", class: "text-[#2E37A4]" },
  ];

  return (
    <div>
      <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 py-4 sm:py-6 md:px-8">
        <div className="pt-2 pb-6 flex justify-between items-center">
          <MyBreakcrumbs data={breadcrumbs} />
          <div className="inline-flex items-center gap-x-3">
            <Button
              variant={selectedTab === "today" ? "contained" : "outlined"}
              onClick={() => {
                setSelectedTab("today");
                const today = new Date();
                setStartDate(getStartOrEndOfDay(today, 'start'));
                setEndDate(getStartOrEndOfDay(today, 'end'));
              }}
            >
              Today
            </Button>
            <Button
              variant={selectedTab === "yesterday" ? "contained" : "outlined"}
              onClick={() => {
                setSelectedTab("yesterday");
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setStartDate(getStartOrEndOfDay(yesterday, 'start'));
                setEndDate(getStartOrEndOfDay(yesterday, 'end'));
              }}
            >
              Yesterday
            </Button>

            <Button
              variant={selectedTab === "custom" ? "contained" : "outlined"}
              onClick={() => {
                if (selectedTab !== "custom") {
                  setSelectedTab("custom");
                  const today = new Date();
                  setStartDate(getStartOrEndOfDay(today, 'start')); // Set start date to today
                  setEndDate(getStartOrEndOfDay(today, 'end')); // Set end date to today
                }
              }}
            >
              Custom
            </Button>
            {selectedTab === "custom" && (
              <>
                <div>
                  <AntDatePicker
                    defaultValue={null}
                    placeholder="select date"
                    value={startDate? dayjs(startDate):startDate}
                    onChange={(date,dateString) => {
                      if (endDate && date > endDate) {
                        setEndDate(null);
                      }
                      setStartDate(dateString);
                    }}
                    disabledFutureDate={true}
                    maxDate={dayjs(endDate)}
                  />
                </div>
                <div>
                  <AntDatePicker
                    defaultValue={null}
                    placeholder="select date"
                    value={endDate? dayjs(endDate):endDate}
                    onChange={(date,dateStringE) => {
                      if (startDate && date < startDate) {
                        setStartDate(null);
                      }
                      setEndDate(dateStringE);
                    }}
                    minDate={dayjs(startDate)}
                  />
                </div>
                {/* {startDate !== null && endDate !== null && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                  }}
                >
                  Clear Dates
                </Button>
              )} */}
              </>
            )}
            <Button
              variant="contained"
              onClick={handleExport}
              style={{ backgroundColor: "#4caf50", color: "white" }}
              startIcon={<CloudDownloadIcon />}
            >
              Export Data
            </Button>
            <SearchInput
              filterInput={filterInput}
              handleFilterChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="flex border-2 border-gray-300 shadow-lg rounded-lg bg-blue-50 mb-6">
          <div className=" flex-1 px-4  py-3">
            <span className="  font-normal"> Start Date: </span>
            <span className=" font-semibold">
              {formatDateGlobal(startDate)}
            </span>
          </div>
          <div className=" flex-1 px-4  py-3 ">
            <span className=" font-normal"> Last Date: </span>
            <span className=" font-semibold">
              {formatDateGlobal(endDate)}
            </span>
          </div>
          <div className=" flex-1 px-4  py-3 ">
            <span className=" font-normal"> Doctors: </span>
            <span className=" font-semibold">
              {selectedDoctor?.label}
            </span>
          </div>
          <div className=" flex-1 px-4  py-3 ">
            <span className=" font-normal"> Total Amount: </span>
            <span className=" font-semibold"> {totalAmount}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md shadow-gray-200">
          <div className="px-5 py-3 flex flex-wrap md:flex-nowrap justify-between items-center gap-y-5">
            <div className=" flex">
              <div className="text-lg font-semibold text-gray-800">Report</div>
            </div>
            <div>
              <CustomSelect
                value={selectedDoctor?.value}
                isSearchable={true}
                isClearable={false}
                isDisabled={false}
                onChange={(e) => setSelectedDoctor(e)}
                options={[
                  { value: null, label: 'All Doctors' },
                  ...(doctors?.map((doctor) => ({
                    value: doctor.doctor_id,
                    label: `${doctor.first_name} ${doctor.last_name}`,
                  })) || []),
                ]}
                className={"w-60"}
              />
            </div>
          </div>

          <div className="overflow-auto">
            <MyTable
              columns={columns}
              data={reports}
              onPrescriptionClick={handlePrescriptionClick}
              filterInput={filterInput}
              handleFilterChange={handleFilterChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Report;
